import {IS_FAN_VOTE_DISABLED} from "modules/constants";
import React, {useCallback} from "react";
import {PrimaryButton, PureButton, SecondaryLink} from "components/Common";
import {useDispatch, useSelector} from "react-redux";
import styled from "@emotion/styled";
import {partial} from "lodash";
import {useTranslation} from "react-i18next";
import {
	getIsAllTeamPicked,
	getIsAllVotePicked,
	getProfile,
	getShareImage,
	getSuccessModalData,
} from "modules/selectors";
import {
	fetchSharingImageClear,
	openContextualPrompt,
	sharingImageLoaded,
	toggleSuccessModal,
} from "modules/actions";
import {GameType, Language, ShareTarget} from "modules/enums";
import {share} from "modules/utils/share";
import {ConnextraType, createConnextraScriptTag} from "modules/utils/analytics";
import {BaseModal} from "components/Modals/BaseModal";
import {ButtonsBox, ModalText, ModalTitle} from "components/Modals/common";
import {Exist, AllStarTeamShareImage} from "components";
import {ReactComponent as DownloadIcon} from "assets/images/icons/download.svg";
import {ReactComponent as FacebookIcon} from "assets/images/icons/facebook.svg";
import {ReactComponent as TwitterIcon} from "assets/images/icons/twitter.svg";

const ShareBox = styled.div`
	display: flex;
	margin-top: 20px;
	justify-content: center;
	gap: 24px;
`;

const getModalText = (isAllVotePicked: boolean, isAllTeamPicked: boolean) => {
	if (isAllVotePicked && isAllTeamPicked) {
		return "modal.success.all_voted_text";
	}

	if (isAllTeamPicked) {
		return "modal.success.voted_text";
	}

	return "modal.success.text";
};

export const SuccessSaveModal: React.FC = () => {
	const dispatch = useDispatch();
	const {t, i18n} = useTranslation();
	const lang = i18n.language as Language;
	const shareImage = useSelector(getShareImage);
	const data = useSelector(getSuccessModalData);
	const isAllTeamPicked = useSelector(getIsAllTeamPicked);
	const isAllVotePicked = useSelector(getIsAllVotePicked);
	const profile = useSelector(getProfile);

	const closeModal = useCallback(() => {
		dispatch(toggleSuccessModal(null));
		dispatch(sharingImageLoaded(false));
		dispatch(fetchSharingImageClear());
		if (profile?.showPrompt) {
			dispatch(openContextualPrompt());
		}
	}, [dispatch, profile]);

	const shareHandler = useCallback(
		(target: ShareTarget) => {
			if (!data) {
				return;
			}

			void share({
				voteId: data.id,
				target,
				locale: lang,
				game: GameType.AllStar,
			});

			createConnextraScriptTag(ConnextraType.SHARE, data.userId);
		},
		[data, lang]
	);

	if (!data) {
		return null;
	}

	const isTeamFull = Object.values(data.lineup).every(Boolean);

	return (
		<BaseModal handleClose={closeModal}>
			<Exist when={isTeamFull}>
				<AllStarTeamShareImage />
			</Exist>
			<ModalTitle>{t("modal.success.title")}</ModalTitle>
			<ModalText>{t(getModalText(isAllVotePicked, isAllTeamPicked))}</ModalText>

			<ButtonsBox>
				<Exist when={!IS_FAN_VOTE_DISABLED}>
					<SecondaryLink to="/fan-vote" onClick={closeModal}>
						{t("modal.success.vote")}
					</SecondaryLink>
				</Exist>

				<Exist when={!isAllTeamPicked}>
					<PrimaryButton onClick={closeModal}>{t("modal.success.close")}</PrimaryButton>
				</Exist>
			</ButtonsBox>

			<Exist when={isTeamFull}>
				<ShareBox>
					{shareImage && (
						<a href={shareImage} download>
							<DownloadIcon />
						</a>
					)}

					<PureButton onClick={partial(shareHandler, ShareTarget.Facebook)}>
						<FacebookIcon />
					</PureButton>

					<PureButton onClick={partial(shareHandler, ShareTarget.Twitter)}>
						<TwitterIcon />
					</PureButton>
				</ShareBox>
			</Exist>
		</BaseModal>
	);
};
